import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../catalyst/button';
import AuthGuard from '../../components/AuthGuard';
import backend from '../../services/backend';
import type { SessionStats } from './useCurationSession';

interface SessionSummaryPanelProps {
  stats: SessionStats;
  onFetchMore: () => void;
}

function RetrainButton({ categoryName }: { categoryName: string }) {
  const [done, setDone] = useState(false);

  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => backend.listCategories().then((res) => res.data),
  });

  const categoryId = categoriesQuery.data?.items.find((c) => c.name === categoryName)?.id ?? null;

  const trainMutation = useMutation({
    mutationFn: () => {
      if (!categoryId) throw new Error(`Category id not found for: ${categoryName}`);
      return backend.trainCategory(String(categoryId));
    },
    onSuccess: () => setDone(true),
  });

  const label = done
    ? `✓ ${categoryName} queued`
    : trainMutation.isPending
      ? `Training ${categoryName}…`
      : `Retrain ${categoryName}`;

  return (
    <Button
      outline
      disabled={trainMutation.isPending || done || !categoryId}
      onClick={() => trainMutation.mutate()}
      className="text-sm"
    >
      {label}
    </Button>
  );
}

export function SessionSummaryPanel({ stats, onFetchMore }: SessionSummaryPanelProps) {
  const { reviewed, corrected, touchedCategories } = stats;
  const categoryCount = touchedCategories.size;
  const touchedList = Array.from(touchedCategories);

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 p-8 text-center space-y-6 max-w-lg mx-auto mt-12">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100 mb-2">Queue complete</h2>
        <p className="text-gray-500 dark:text-zinc-400 text-sm">
          {reviewed} reviewed · {corrected} corrected
          {categoryCount > 0 && ` across ${categoryCount} ${categoryCount === 1 ? 'category' : 'categories'}`}
        </p>
      </div>

      {touchedList.length > 0 && (
        <AuthGuard roles={['admin', 'supervisor']}>
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 dark:text-zinc-400 uppercase tracking-wide">
              Retrain affected categories
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {touchedList.map((name) => (
                <RetrainButton key={name} categoryName={name} />
              ))}
            </div>
          </div>
        </AuthGuard>
      )}

      <Button onClick={onFetchMore}>Fetch more</Button>
    </div>
  );
}
